// Master Events Data Integration
// Processed from events_master_with_format_and_geo.json
// 1,325 Washington D.C. events with auto-categorization
// Plus 75 NYC Master Itinerary locations

import { processMasterEvents, categorizeEvent, clusterEventsByLocation } from './masterEventsProcessor';
import { NYC_MASTER_LOCATIONS, NYC_CATEGORIES, NYC_REGION_INFO } from './nycMasterLocations';

// Load the full dataset directly

// Load full dataset synchronously on module load
const loadFullDataset = async () => {
  try {
    console.log('🔄 Loading full events dataset...');
    
    // Load from public directory
    const response = await fetch('/events_master_with_format_and_geo.json');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const rawData = await response.json();
    console.log('📊 Full events data loaded:', rawData.length, 'events');
    
    // Process the full dataset
    const processedEvents = processMasterEvents(rawData);
    
    console.log('✅ Full events processing complete:', {
      totalEvents: processedEvents.length,
      categories: [...new Set(processedEvents.map(e => e.categoryPrimary))],
      formats: [...new Set(processedEvents.map(e => e.format))],
      locations: [...new Set(processedEvents.map(e => e.venue))]
    });
    
    return processedEvents;
  } catch (error) {
    console.error('❌ Error loading full events data:', error);
    console.log('🔄 Falling back to sample data...');
    
    // Fallback to sample data
    const sampleData = [
      {
        "event_title": "RAMW Restaurant Week: Together We Dine",
        "description": "Month‑long dining specials and events across DC restaurants.",
        "start_time": "Oct 1–31, 2025",
        "location/address": "Various locations across Washington, DC",
        "format": "In-person",
        "latitude": 38.9072,
        "longitude": -77.0369
      },
      {
        "event_title": "Neko Case",
        "description": "Singer‑songwriter in concert; noted for work with The New Pornographers and acclaimed solo albums.",
        "start_time": "Oct 2, 2025 • 8:00 PM",
        "location/address": "Lincoln Theatre, 1215 U St NW, Washington, DC 20009",
        "format": "In-person",
        "latitude": 38.9072,
        "longitude": -77.0369
      },
      {
        "event_title": "Economic Policy Forum",
        "description": "Discussion on economic policy and trade relations with expert panelists.",
        "start_time": "Oct 15, 2025 • 2:00 PM",
        "location/address": "Brookings Institution, Washington, DC",
        "format": "In-person",
        "latitude": 38.9072,
        "longitude": -77.0369
      }
    ];
    
    return processMasterEvents(sampleData);
  }
};

// Initialize with empty array, will be populated by async loading
let masterEvents = [];

// Load the dataset asynchronously and update the array
loadFullDataset().then(events => {
  masterEvents.length = 0; // Clear array
  masterEvents.push(...events); // Add all DC events
  
  // Add NYC locations to the master events
  masterEvents.push(...NYC_MASTER_LOCATIONS);
  
  console.log('✅ Master Events Loaded:', masterEvents.length, 'events');
  console.log('🏛️ DC Events:', events.length);
  console.log('🗽 NYC Locations:', NYC_MASTER_LOCATIONS.length);
  
  // Expose on window for Events component
  window.masterEventsData = masterEvents
  window.MASTER_EVENTS = masterEvents
  
  // Trigger a custom event to notify components
  window.dispatchEvent(new CustomEvent('masterEventsLoaded', { 
    detail: { count: masterEvents.length } 
  }));
}).catch(error => {
  console.error('❌ Error loading master events:', error);
  masterEvents.length = 0;
});

// Export the processed events (will be updated by async loading)
export const MASTER_EVENTS = masterEvents;

// Create location clusters for better organization
export const EVENT_CLUSTERS = clusterEventsByLocation(MASTER_EVENTS);

// Export categorized events for easy access
export const getEventsByCategory = (category, subcategory = null) => {
  return MASTER_EVENTS.filter(event => {
    const matchesCategory = !category || event.categoryPrimary === category;
    const matchesSubcategory = !subcategory || event.categorySecondary === subcategory;
    return matchesCategory && matchesSubcategory;
  });
};

// Export online events
export const getOnlineEvents = () => {
  return MASTER_EVENTS.filter(event => event.format === 'Online');
};

// Export in-person events
export const getInPersonEvents = () => {
  return MASTER_EVENTS.filter(event => event.format === 'In-person');
};

// Export NYC-specific functions
export const getNYCEvents = () => {
  return MASTER_EVENTS.filter(event => event.region === 'NYC');
};

export const getDCEvents = () => {
  return MASTER_EVENTS.filter(event => !event.region || event.region === 'DC');
};

export const getEventsByRegion = (region) => {
  if (region === 'NYC') return getNYCEvents();
  if (region === 'DC') return getDCEvents();
  return MASTER_EVENTS;
};

// Export NYC categories and region info
export { NYC_CATEGORIES, NYC_REGION_INFO };

// Debug logging
console.log('🎯 Master Events Loaded:', MASTER_EVENTS.length, 'events');
console.log('📍 Location Clusters:', Object.keys(EVENT_CLUSTERS).map(location => 
  `${location}: ${EVENT_CLUSTERS[location].length}`
));
console.log('🌐 Online Events:', getOnlineEvents().length);
console.log('🏢 In-Person Events:', getInPersonEvents().length);